import { DetailsPanelType } from "PFApp/booking/components/details_panel/details_panel.types";
import { getGrowlId } from "PFApp/stores/growl_store";
import { PanelKey, SidePanelType } from "PFApp/stores/side_panels_store";
import { BOOKING_CATEGORY_ERROR_KEY, PHASES_ERROR_KEY } from "PFComponents/availability";
import { UseFormClearErrors, UseFormSetError } from "react-hook-form";

import { BookingDataItem, BookingDataItemLimitation, BookingFormMode } from "./booking_form.types";
import { BookingFormValues } from "./booking_form_provider";

export const isNotLimited = ({ limitation }: BookingDataItem) =>
  limitation === BookingDataItemLimitation.None;

export const getBookingFormDetailsPanelKey = (mode: BookingFormMode): PanelKey =>
  `${SidePanelType.BookingDetails}_${DetailsPanelType.BookingForm}_${DetailsPanelType.BookingForm}_${mode}`;

export const isEditMode = (mode: BookingFormMode | undefined): boolean =>
  !!mode && [BookingFormMode.EditSingle, BookingFormMode.EditAll].includes(mode);

export const getHeaderTitleTranslationKey = (mode: BookingFormMode | undefined) => {
  if (isEditMode(mode)) {
    return "bookingModule:bookings.update.editBooking";
  }
  if (mode && [BookingFormMode.CloneSingle, BookingFormMode.CloneAll].includes(mode)) {
    return "bookingModule:bookings.clone.cloneBooking";
  }
  return "bookingModule:bookings.create.createBooking";
};

export const getSubmitSuccessMessageTranslationKey = (
  mode: BookingFormMode | undefined,
  isAffectingGroup: boolean
): {
  actionId: string;
  messageKey:
    | "bookings.create.bulk.success"
    | "bookings.update.success"
    | "bookings.clone.success"
    | "bookings.create.success";
} => {
  if (isAffectingGroup) {
    return {
      actionId: getGrowlId("bm-booking-form-create-bulk"),
      messageKey: "bookings.create.bulk.success"
    };
  }
  if (isEditMode(mode)) {
    return {
      actionId: getGrowlId("bm-booking-form-update"),
      messageKey: "bookings.update.success"
    };
  }
  if (mode && [BookingFormMode.CloneSingle, BookingFormMode.CloneAll].includes(mode)) {
    return {
      actionId: getGrowlId("bm-booking-form-clone"),
      messageKey: "bookings.clone.success"
    };
  }
  return {
    actionId: getGrowlId("bm-booking-form-create"),
    messageKey: "bookings.create.success"
  };
};

export const getSubmitErrorMessageTranslationKey = (mode: BookingFormMode | undefined) => {
  if (isEditMode(mode)) {
    return "bookings.update.error";
  }
  if (mode && [BookingFormMode.CloneSingle, BookingFormMode.CloneAll].includes(mode)) {
    return "bookings.clone.error";
  }
  return "bookings.create.error";
};

export const setKnownErrors = (
  mode: BookingFormMode,
  bookings: BookingDataItem[],
  responseErrors: Record<string, string>,
  setError: UseFormSetError<BookingFormValues>,
  clearErrors: UseFormClearErrors<BookingFormValues>,
  action: "potential-warnings" | "submit"
) => {
  const { categoryErrors, phaseSourceIdErrors, endDateErrors } = bookings.filter(isNotLimited).reduce(
    (result, current, index) => {
      const globalCategoryErrorKey = BOOKING_CATEGORY_ERROR_KEY;
      const categoryErrorKey = `bookings[${index}][${BOOKING_CATEGORY_ERROR_KEY}]`;
      const phaseErrorKey = `bookings[${index}]${PHASES_ERROR_KEY}`;
      const endDateErrorKey = `bookings[${index}][end_date]`;
      const categoryError = responseErrors[categoryErrorKey] ?? responseErrors[globalCategoryErrorKey];
      const phaseError = responseErrors[phaseErrorKey];
      const endDateError = responseErrors[endDateErrorKey];

      return {
        categoryErrors: {
          ...result.categoryErrors,
          ...(categoryError ? { [current.key]: categoryError } : {})
        },
        phaseSourceIdErrors: {
          ...result.phaseSourceIdErrors,
          ...(phaseError ? { [current.key]: phaseError } : {})
        },
        endDateErrors: {
          ...result.endDateErrors,
          ...(endDateError ? { [current.key]: endDateError } : {})
        }
      };
    },
    { categoryErrors: {}, phaseSourceIdErrors: {}, endDateErrors: {} }
  );
  bookings.forEach((item, index) => {
    const categoryErrorKey =
      mode === BookingFormMode.Create
        ? `globalCategoryErrorAPI.${action}`
        : `bookings.${index}.categoryErrorAPI.${action}`;
    if (categoryErrors[item.key]) {
      setError(categoryErrorKey as keyof BookingFormValues, {
        type: "custom",
        message: categoryErrors[item.key]
      });
    } else {
      clearErrors(categoryErrorKey as keyof BookingFormValues);
    }
    if (phaseSourceIdErrors[item.key]) {
      setError(`bookings.${index}.phaseSourceIdErrorAPI.${action}` as keyof BookingFormValues, {
        type: "custom",
        message: phaseSourceIdErrors[item.key]
      });
    } else {
      clearErrors(`bookings.${index}.phaseSourceIdErrorAPI.${action}` as keyof BookingFormValues);
    }
    if (endDateErrors[item.key]) {
      setError(`bookings.${index}.endDateErrorAPI.${action}` as keyof BookingFormValues, {
        type: "custom",
        message: endDateErrors[item.key]
      });
    } else {
      clearErrors(`bookings.${index}.endDateErrorAPI.${action}` as keyof BookingFormValues);
    }
  });
};

export const clearKnownErrors = (
  mode: BookingFormMode,
  bookings: BookingDataItem[],
  clearErrors: UseFormClearErrors<BookingFormValues>,
  action: "potential-warnings" | "submit"
) => {
  if (mode === BookingFormMode.Create) {
    clearErrors(`globalCategoryErrorAPI.${action}` as keyof BookingFormValues);
    bookings.forEach((_, index) =>
      clearErrors([
        `bookings.${index}.phaseSourceIdErrorAPI.${action}` as keyof BookingFormValues,
        `bookings.${index}.endDateErrorAPI.${action}` as keyof BookingFormValues
      ])
    );
  } else {
    bookings.forEach((_, index) =>
      clearErrors([
        `bookings.${index}.categoryErrorAPI.${action}` as keyof BookingFormValues,
        `bookings.${index}.phaseSourceIdErrorAPI.${action}` as keyof BookingFormValues,
        `bookings.${index}.endDateErrorAPI.${action}` as keyof BookingFormValues
      ])
    );
  }
};
